import { useEffect, useRef } from 'react';
import { MAX_FORWARD_DAYS, STRIP_BACK_DAYS, dateByOffset, dayPhrase } from '@/lib/mealDay';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Полоска выбора дня: неделя назад … два дня вперёд.
 * Одна на все места, где еду записывают, — экран записи, «Мои блюда», история,
 * копирование приёма. Раньше в каждом стояли свои две кнопки «Сегодня / Вчера».
 * С 07.10.2026 она же листает дневник (там длиннее назад и с отметками дней).
 *
 * Будущие дни обведены пунктиром: это план, а не съеденное.
 */
export function DayStrip({
  value,
  onChange,
  bleed = false,
  hint = true,
  back = STRIP_BACK_DAYS,
  forward = MAX_FORWARD_DAYS,
  marks,
}: {
  /** Смещение от сегодня: 0 сегодня, -1 вчера, +1 завтра */
  value: number;
  onChange: (offset: number) => void;
  /** Полоска выходит за боковые поля экрана (px-5), чтобы листалась от края до края */
  bleed?: boolean;
  /** Подпись под полоской о том, куда запишется */
  hint?: boolean;
  /** Сколько дней показать назад и вперёд */
  back?: number;
  forward?: number;
  /** Смещения дней, где уже есть записи, — точка под числом */
  marks?: Set<number>;
}) {
  const { haptic } = useTelegram();
  const ref = useRef<HTMLDivElement>(null);
  const first = useRef(true);

  // Пришли с более ранней датой, чем показывает полоска, — ставим её первой
  const days: number[] = [];
  for (let o = -back; o <= forward; o++) days.push(o);
  if (value < -back) days.unshift(value);
  if (value > forward) days.push(value);

  useEffect(() => {
    // Выбранный день — в центр. Первый раз без анимации, дальше плавно:
    // в дневнике день меняется и свайпом, полоска должна за ним успевать.
    const el = ref.current?.querySelector<HTMLElement>('[data-selected="true"]');
    const box = ref.current;
    if (el && box) {
      // Двигаем только саму полоску: scrollIntoView прокрутил бы и страницу.
      const left = el.offsetLeft - box.clientWidth / 2 + el.clientWidth / 2;
      box.scrollTo({ left, behavior: first.current ? 'auto' : 'smooth' });
    }
    first.current = false;
  }, [value]);

  return (
    <div>
      <div ref={ref} className={`relative overflow-x-auto ${bleed ? '-mx-5 px-5' : ''}`} style={{ scrollbarWidth: 'none' }}>
        <div className="flex gap-1.5" style={{ width: 'max-content' }}>
          {days.map((o) => {
            const d = dateByOffset(o);
            const on = o === value;
            const top = o === 0 ? 'Сегодня' : o === -1 ? 'Вчера' : o === 1 ? 'Завтра'
              : d.toLocaleDateString('ru-RU', { weekday: 'short' });
            const marked = marks?.has(o);
            return (
              <button
                key={o}
                type="button"
                data-selected={on}
                onClick={() => { onChange(o); haptic('light'); }}
                className={`relative shrink-0 min-w-[58px] px-2 pt-1.5 ${marks ? 'pb-2.5' : 'pb-1.5'} rounded-xl border transition-colors flex flex-col items-center ${
                  on
                    ? 'bg-emerald-glow text-bg border-emerald-glow'
                    : o > 0
                      ? 'bg-bg-card text-ink-muted border-dashed border-line'
                      : 'bg-bg-card text-ink-muted border-line'
                }`}
              >
                <span className="text-[10px] font-medium first-letter:uppercase">{top}</span>
                <span className={`text-sm font-display font-bold ${on ? '' : 'text-ink'}`}>{d.getDate()}</span>
                {marks && (
                  <span
                    className={`absolute bottom-1 w-1 h-1 rounded-full ${
                      marked ? (on ? 'bg-bg' : 'bg-emerald-glow') : 'bg-transparent'
                    }`}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>
      {hint && value !== 0 && (
        <p className="text-[11px] text-ink-dim mt-2">
          {value > 0
            ? `📅 Запишется в дневник ${dayPhrase(value)} как запланированное — в серию и достижения пойдёт, когда день наступит`
            : `💡 Запишется в дневник ${dayPhrase(value)}`}
        </p>
      )}
    </div>
  );
}
